import { JupyterFrontEnd } from "@jupyterlab/application";
import { whenExists } from "./whenExists";
import { IDocumentManager } from "@jupyterlab/docmanager";
import {
  INotebookModel,
  INotebookTracker,
  NotebookPanel,
} from "@jupyterlab/notebook";
import { IThemeManager } from "@jupyterlab/apputils";
import { ISettingRegistry } from "@jupyterlab/settingregistry";
import { DocumentRegistry } from "@jupyterlab/docregistry";

/** PageConfig allows control over the JupyterLab PageConfig namespace. */
interface PageConfig {
  getOption(name: string): string;
  setOption(name: string, value: string): string;
}

/** A builtin window.jupyterlab exposed by JupyterLab. */
export interface JupyterLabAppWindow extends Window {
  jupyterlab: JupyterFrontEnd;
  pageconfig: PageConfig;
}

/** Notified when JupyterLab panel initializes. */
export type OnCreateNewCallback = (
  panel: NotebookPanel,
  context: DocumentRegistry.IContext<INotebookModel>
) => void;

/**
 * The Kaggle JupyterLab global interface for exposing APIs to @kaggle/web.
 */
export interface JupyterLabGlobal {
  app: JupyterFrontEnd;
  themeManager: IThemeManager;
  notebookTracker: INotebookTracker;
  documentManager: IDocumentManager;
  panel?: NotebookPanel;
  context?: DocumentRegistry.IContext<INotebookModel>;
  onCreateNew: (callback: OnCreateNewCallback) => void;
  subscribers: OnCreateNewCallback[];
  registry: ISettingRegistry;
  updatePageConfig: (n: string, v: string) => void;
}

/** Returns a promise that will resolve to JupyterLab API when ready. */
export const getJupyterLab = () => {
  const jlaw = window as unknown as JupyterLabAppWindow;
  return whenExists(() => {
    if (jlaw.jupyterlab) {
      if (!cacheJL) {
        cacheJL = appToServices(jlaw.jupyterlab, jlaw.pageconfig);
      }
      return cacheJL;
    }

    return null;
  }, 1000);
};

let cacheJL: Promise<JupyterLabGlobal>;

// Create a JupyterLabGlobal from a JupyterFrontEnd.
// This allows us to drop needing an extension, since we can just load them from the plugin-map provided by window.jupyterlab itself.
const appToServices = async (
  app: JupyterFrontEnd,
  pc: PageConfig
): Promise<JupyterLabGlobal> => {
  const dm = await whenExists(
    () =>
      (app as any)._pluginMap["@jupyterlab/docmanager-extension:plugin"]
        ?.service as IDocumentManager,
    1000
  );
  const nb = await whenExists(
    () =>
      (app as any)._pluginMap["@jupyterlab/notebook-extension:tracker"]
        ?.service as INotebookTracker,
    1000
  );
  const ctx = await whenExists(async () => {
    const nbw = await whenExists(() => nb.currentWidget, 1000);
    return dm.contextForWidget(
      nbw
    ) as DocumentRegistry.IContext<INotebookModel>;
  }, 1000);
  const tm = await whenExists(
    () =>
      (app as any)._pluginMap["@jupyterlab/apputils-extension:themes"]
        ?.service as IThemeManager,
    1000
  );
  const sr = await whenExists(
    () =>
      (app as any)._pluginMap["@jupyterlab/apputils-extension:settings"]
        ?.service as ISettingRegistry,
    1000
  );
  const subscribers: OnCreateNewCallback[] = [];
  return {
    app,
    subscribers,
    themeManager: tm,
    notebookTracker: nb,
    documentManager: dm,
    registry: sr,
    context: ctx,
    updatePageConfig: pc ? pc.setOption : () => {},
    onCreateNew: subscribers.push,
  };
};
